// JD library (Phase 2) — reusable job-description templates HR/recruiters
// maintain and prefill into new job postings.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Plus, Trash2, Pencil, FileText } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const blank = { title: '', dept: '', jdText: '', jdSkills: '', minYears: '', companyWide: true };

export default function JdLibrary({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.jdTemplates().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const edit = (t) => setForm({ ...t, jdSkills: (t.jdSkills || []).join(', '), minYears: t.minYears ?? '', companyWide: !t.entityId });
  const save = async () => {
    if (!form.title.trim() || !form.jdText.trim() || busy) return; setBusy(true);
    const payload = { ...form, jdSkills: form.jdSkills };
    try {
      if (form.id) await api.updateJdTemplate(form.id, payload); else await api.createJdTemplate(payload);
      showToast?.('Template saved'); setForm(null); load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const remove = async (t) => { try { await api.deleteJdTemplate(t.id); load(); } catch (e) { showToast?.(e.message); } };

  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="JD Library" sub="Reusable job-description templates" />
        <PrimaryBtn onClick={() => setForm({ ...blank })}><Plus size={14} style={{ marginRight: 6 }} /> New template</PrimaryBtn>
      </div>
      {rows.length === 0 ? <Card><EmptyState icon={FileText} title="No templates" desc="Create a JD template to prefill new job postings." /></Card> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((t) => (
            <Card key={t.id} style={{ padding: 16 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: AG.text }}>{t.title} {!t.entityId && <Pill tone="neutral">Company-wide</Pill>}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{t.dept || '—'}{t.minYears != null ? ` · ${t.minYears}+ yrs` : ''}{t.jdSkills?.length ? ` · ${t.jdSkills.length} skills` : ''}</div>
                  <div style={{ fontSize: 12.5, color: AG.n600, marginTop: 6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.jdText}</div>
                </div>
                <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                  <button onClick={() => edit(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Pencil size={15} /></button>
                  <button onClick={() => remove(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.id ? 'Edit template' : 'New JD template'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              <TextField size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <TextField size="small" label="Department" value={form.dept} onChange={(e) => setForm({ ...form, dept: e.target.value })} />
                <TextField size="small" type="number" label="Min. years" value={form.minYears} onChange={(e) => setForm({ ...form, minYears: e.target.value })} inputProps={{ min: 0 }} />
              </div>
              <TextField size="small" label="Job description" multiline minRows={4} value={form.jdText} onChange={(e) => setForm({ ...form, jdText: e.target.value })} />
              <TextField size="small" label="Screening skills (comma-separated)" value={form.jdSkills} onChange={(e) => setForm({ ...form, jdSkills: e.target.value })} placeholder="React, Node, SQL" />
              <FormControlLabel control={<Checkbox checked={!!form.companyWide} onChange={(e) => setForm({ ...form, companyWide: e.target.checked })} />} label="Available company-wide" />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={save} disabled={busy}>Save</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
